import { useEffect, useRef, useState } from 'react';
import { HiDotsHorizontal } from 'react-icons/hi';
import { IoClose } from 'react-icons/io5';

import type { DBDocument } from '@core/types/types.db';
import type {
  ConnectionDisconnectEndpoint,
  ConnectionInfo,
  ConnectionSetChannelEndpoint,
  ConnectionSyncEndpoint,
} from '@core/types/types.endpoint.connection';
import type { GoogleSelectFilesEndpoint } from '@core/types/types.endpoint.google';
import { Button } from '@frontend/components/ui/button';
import { Checkbox } from '@frontend/components/ui/checkbox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@frontend/components/ui/popover';
import { GoogleFilePicker } from '@frontend/Settings/components/GoogleFilePicker';
import { ConfirmDialog } from '@frontend/shared/components/ConfirmDialog/ConfirmDialog';
import { useFlag } from '@frontend/shared/hooks/shared.hook.useFlag';
import { useDirectoryChannels } from '@frontend/shared/hooks/useDirectory';
import { useSingleSearchParam } from '@frontend/shared/hooks/useSingleSearchParam';
import { useSubmitter } from '@frontend/shared/hooks/useSubmitter';
import { callApi } from '@frontend/shared/util/util.callApi';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';

interface GoogleFileLite {
  id: string;
  name: string;
  mimeType: string;
  webViewLink: string;
}

function googleFileFromDocument(doc: DBDocument): GoogleFileLite | null {
  if (doc.metadata.type !== 'google_drive') return null;
  return {
    id: doc.document_id,
    name: doc.title,
    mimeType: doc.metadata.mimeType,
    webViewLink: doc.metadata.webViewLink,
  };
}

const ICON_BY_TYPE: Record<ConnectionInfo['connectionType'], string> = {
  google_drive: '/assets/services/gdrive.png',
  notion: '/assets/services/notion.ico',
  confluence: '/assets/services/confluence.ico',
  webcrawler: '/assets/services/webcrawler.svg',
};

const TITLE_BY_TYPE: Record<ConnectionInfo['connectionType'], string> = {
  google_drive: 'Google Drive',
  notion: 'Notion',
  confluence: 'Confluence',
  webcrawler: 'Website',
};

export function SettingsNativeConnectionRow({
  connection,
  refetch,
  isReadOnly = false,
}: {
  connection: ConnectionInfo;
  refetch: () => Promise<void>;
  isReadOnly?: boolean;
}) {
  const channelBeta = useFlag('channel_beta');
  const channels = useDirectoryChannels({
    excludeDeleted: true,
    requireQbInChannel: true,
  });

  // Legacy rows (pre-multi-channel migration) may come back without `channels`.
  // Default to "all channels" so the UI renders instead of crashing.
  const channelsList = connection.channels ?? [];
  const isAllChannels =
    channelsList.length === 0 || channelsList.includes('all');
  const connectionChannels = isAllChannels
    ? []
    : channelsList.filter((c) => c !== 'all');

  const [isOpen, setIsOpen] = useState(false);
  const [editingAllChannels, setEditingAllChannels] = useState(isAllChannels);
  const [editingSelectedChannels, setEditingSelectedChannels] =
    useState<string[]>(connectionChannels);

  const handleOpen = () => {
    setEditingAllChannels(isAllChannels);
    setEditingSelectedChannels(connectionChannels);
    setIsOpen(true);
  };

  const handleCancel = () => {
    setEditingAllChannels(isAllChannels);
    setEditingSelectedChannels(connectionChannels);
    setIsOpen(false);
  };

  const disconnect = useSubmitter(async () => {
    await callApi<ConnectionDisconnectEndpoint>('/connection/disconnect', {
      connectionId: connection.connectionId,
    });
    await refetch();
  });

  const updateChannel = useSubmitter(async () => {
    const channelsValue =
      editingAllChannels || editingSelectedChannels.length === 0
        ? ['all']
        : editingSelectedChannels;
    const [err] = await callApi<ConnectionSetChannelEndpoint>(
      '/connection/setChannel',
      {
        connectionId: connection.connectionId,
        channels: channelsValue,
      },
    );
    if (!err) {
      setIsOpen(false);
      await refetch();
    }
  });

  const submitGoogleFiles = useSubmitter(async (files: GoogleFileLite[]) => {
    const [err] = await callApi<GoogleSelectFilesEndpoint>(
      '/google/selectFiles',
      {
        connectionId: connection.connectionId,
        files,
      },
    );
    if (!err) {
      await refetch();
    }
  });

  const googleFiles = connection.documents
    .map(googleFileFromDocument)
    .filter((f): f is GoogleFileLite => f !== null);

  const addFiles = (
    picked: { id: string; name: string; mimeType: string; url: string }[],
  ) => {
    const byId = new Map<string, GoogleFileLite>();
    for (const f of googleFiles) byId.set(f.id, f);
    for (const f of picked) {
      byId.set(f.id, {
        id: f.id,
        name: f.name,
        mimeType: f.mimeType,
        webViewLink: f.url,
      });
    }
    submitGoogleFiles.submit(Array.from(byId.values()));
  };

  const [pendingDeleteFileId, setPendingDeleteFileId] = useState<string | null>(
    null,
  );
  const pendingDeleteFile = pendingDeleteFileId
    ? googleFiles.find((f) => f.id === pendingDeleteFileId)
    : null;

  const removeFile = (fileId: string) => {
    setPendingDeleteFileId(fileId);
  };

  const confirmRemoveFile = () => {
    if (!pendingDeleteFileId) return;
    submitGoogleFiles.submit(
      googleFiles.filter((f) => f.id !== pendingDeleteFileId),
    );
    setPendingDeleteFileId(null);
  };

  const syncNow = useSubmitter(async () => {
    const [err] = await callApi<ConnectionSyncEndpoint>('/connection/sync', {
      connectionId: connection.connectionId,
    });
    if (!err) {
      await refetch();
    }
  });

  const isSyncing = connection.status === 'syncing';

  // Backend sync runs async in a worker; poll until status flips off 'syncing'
  // so the row doesn't stay stuck in the "Syncing..." state until manual reload.
  const refetchRef = useRef(refetch);
  useEffect(() => {
    refetchRef.current = refetch;
  });
  useEffect(() => {
    if (!isSyncing) return;
    const interval = setInterval(() => {
      void refetchRef.current();
    }, 5000);
    return () => clearInterval(interval);
  }, [isSyncing]);

  const [newConnectionParam, setNewConnectionParam] = useSingleSearchParam(
    'newGoogleConnection',
  );
  const shouldAutoOpenPicker =
    connection.connectionType === 'google_drive' &&
    newConnectionParam === connection.connectionId;
  useEffect(() => {
    if (shouldAutoOpenPicker) {
      setNewConnectionParam(undefined, { replace: true });
    }
  }, [shouldAutoOpenPicker, setNewConnectionParam]);

  const hasChanges = () => {
    if (editingAllChannels !== isAllChannels) return true;
    if (editingAllChannels) return false;
    if (editingSelectedChannels.length !== connectionChannels.length)
      return true;
    return editingSelectedChannels.some(
      (id) => !connectionChannels.includes(id),
    );
  };

  const getChannelDisplayText = () => {
    if (isAllChannels) return 'All channels';
    if (connectionChannels.length === 1) {
      const channel = channels.find((ch) => ch.id === connectionChannels[0]);
      return channel ? `#${channel.name}` : '1 channel';
    }
    return `${connectionChannels.length} channels`;
  };

  const documentCount = connection.documentCount;
  const documentNoun = documentCount === 1 ? 'file' : 'files';
  const isGoogle = connection.connectionType === 'google_drive';

  return (
    <>
      <ConfirmDialog
        isOpen={pendingDeleteFileId !== null}
        onClose={() => setPendingDeleteFileId(null)}
        onSuccess={confirmRemoveFile}
        title="Remove file"
        description={`Remove "${pendingDeleteFile?.name ?? ''}" from Question Base? It will no longer be used to answer questions.`}
      />
      <div className="relative mt-2 flex items-center">
        <img
          src={ICON_BY_TYPE[connection.connectionType]}
          className="mr-2 size-6"
          alt=""
        />
        <span>{connection.displayName}</span>

        <span className="ml-2 flex items-center gap-1 text-xs text-gray-500">
          {isSyncing ? (
            <>
              <Loader2 className="size-3 animate-spin" />
              Syncing {documentCount} {documentNoun}...
            </>
          ) : (
            <>
              ({documentCount} {documentNoun})
            </>
          )}
        </span>

        {channelBeta && (
          <span className="ml-2 text-xs text-gray-400">
            • {getChannelDisplayText()}
          </span>
        )}

        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <Button
              aria-label="Options"
              variant="ghost"
              size="icon"
              className={`ml-2 ${isReadOnly ? 'hidden' : ''}`}
              onClick={handleOpen}
              disabled={isReadOnly}
            >
              <HiDotsHorizontal className="size-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent side="right" className="w-[340px]">
            <div className="flex flex-col gap-2">
              <h3 className="text-sm font-medium">
                {TITLE_BY_TYPE[connection.connectionType]}
              </h3>
              <p className="text-xs">{connection.displayName}</p>
              <p className="text-xs text-gray-500">
                Last synced:{' '}
                {connection.lastSyncedAt
                  ? new Date(connection.lastSyncedAt).toLocaleString()
                  : 'Never'}
              </p>

              <p className="mt-3 text-xs font-medium">
                Synced {documentNoun} ({documentCount})
              </p>
              {connection.documents.length === 0 ? (
                <p className="text-xs text-gray-500">
                  No {documentNoun} synced yet.
                </p>
              ) : (
                <ul className="flex max-h-48 flex-col gap-1 overflow-y-auto">
                  {connection.documents.map((d) => {
                    const status = d.syncStatus ?? 'ok';
                    const isPending = status === 'pending';
                    const isError =
                      status === 'not_found' ||
                      status === 'access_denied' ||
                      status === 'unsupported' ||
                      status === 'failed';
                    return (
                      <li
                        key={d.document_id}
                        className={`group flex items-start gap-1 rounded px-1 py-0.5 text-xs ${
                          isError ? 'bg-red-50' : 'hover:bg-gray-50'
                        }`}
                      >
                        <span className="mt-[2px] shrink-0">
                          {isPending ? (
                            <Loader2 className="size-3 animate-spin text-gray-400" />
                          ) : isError ? (
                            <AlertCircle className="size-3 text-red-600" />
                          ) : (
                            <CheckCircle2 className="size-3 text-green-600" />
                          )}
                        </span>
                        <div className="flex-1 overflow-hidden">
                          <a
                            href={d.source_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block truncate hover:underline"
                            title={d.title}
                          >
                            {d.title}
                          </a>
                          {isError && d.syncError && (
                            <p className="truncate text-[11px] text-red-700">
                              {d.syncError}
                            </p>
                          )}
                        </div>
                        {isGoogle && (
                          <button
                            type="button"
                            aria-label={`Remove ${d.title}`}
                            onClick={() => removeFile(d.document_id)}
                            disabled={submitGoogleFiles.isSubmitting}
                            className="shrink-0 rounded p-0.5 hover:bg-gray-200 disabled:opacity-50"
                          >
                            <IoClose className="size-3.5" />
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}

              <div className="mt-2 flex gap-2">
                {isGoogle && (
                  <GoogleFilePicker
                    onFilesPicked={addFiles}
                    googleEmail={connection.displayName}
                    trigger={
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={submitGoogleFiles.isSubmitting}
                      >
                        {submitGoogleFiles.isSubmitting
                          ? 'Saving...'
                          : 'Add files'}
                      </Button>
                    }
                  />
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => syncNow.submit()}
                  disabled={
                    isSyncing || syncNow.isSubmitting || documentCount === 0
                  }
                >
                  {isSyncing || syncNow.isSubmitting ? (
                    <>
                      <Loader2 className="mr-1 size-3 animate-spin" />
                      Syncing...
                    </>
                  ) : (
                    'Sync now'
                  )}
                </Button>
              </div>
              {isGoogle && (
                <p className="text-[11px] text-gray-500">
                  Tip: Hold ⌘ (or Ctrl) to select multiple files.
                </p>
              )}

              {channelBeta && (
                <>
                  <p className="mt-6 text-xs font-medium">
                    Answer questions in
                  </p>
                  <div className="mt-2 flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={editingAllChannels}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setEditingAllChannels(true);
                            setEditingSelectedChannels([]);
                          }
                        }}
                      />
                      <label className="text-sm">All channels</label>
                    </div>
                    <div className="flex flex-col gap-2 pl-4">
                      {channels.map((channel) => (
                        <div
                          key={channel.id}
                          className="flex items-center gap-2"
                        >
                          <Checkbox
                            checked={
                              !editingAllChannels &&
                              editingSelectedChannels.includes(channel.id)
                            }
                            onCheckedChange={(checked) => {
                              if (editingAllChannels && checked) {
                                setEditingAllChannels(false);
                                setEditingSelectedChannels([channel.id]);
                              } else {
                                let newChannels: string[];
                                if (checked) {
                                  newChannels = [
                                    ...editingSelectedChannels,
                                    channel.id,
                                  ];
                                } else {
                                  newChannels = editingSelectedChannels.filter(
                                    (id) => id !== channel.id,
                                  );
                                }
                                if (newChannels.length === 0) {
                                  setEditingAllChannels(true);
                                  setEditingSelectedChannels([]);
                                } else {
                                  setEditingSelectedChannels(newChannels);
                                }
                              }
                            }}
                          />
                          <label className="text-sm">#{channel.name}</label>
                        </div>
                      ))}
                    </div>
                  </div>
                  {hasChanges() && (
                    <div className="mt-4 flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleCancel}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        variant="default"
                        onClick={() => updateChannel.submit()}
                        disabled={updateChannel.isSubmitting}
                        className="flex-1"
                      >
                        {updateChannel.isSubmitting ? 'Saving...' : 'Save'}
                      </Button>
                    </div>
                  )}
                </>
              )}

              <Button
                size="sm"
                variant="destructive"
                className="mt-10"
                onClick={() => disconnect.submit()}
                disabled={disconnect.isSubmitting}
              >
                {disconnect.isSubmitting ? 'Disconnecting...' : 'Disconnect'}
              </Button>
            </div>
          </PopoverContent>
        </Popover>

        {isGoogle && documentCount === 0 && !isReadOnly && (
          <GoogleFilePicker
            onFilesPicked={addFiles}
            autoOpen={shouldAutoOpenPicker}
            googleEmail={connection.displayName}
            trigger={
              <Button size="sm" variant="default" className="ml-2">
                {submitGoogleFiles.isSubmitting ? 'Saving...' : 'Select files'}
              </Button>
            }
          />
        )}
      </div>
    </>
  );
}
